import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import { StandardURL } from "../App";

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("add"); // "add" or "update"
  const [modalData, setModalData] = useState({
    code: "",
    amount: "",
    expiry_date: "",
    usage_limit: "",
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${StandardURL}/iNyumba/promo-codes/fetchall`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPromoCodes(response.data.response);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      toast.error(error.response?.data?.response || "Failed to fetch promo codes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async () => {
    const { code, amount, expiry_date, usage_limit } = modalData;
    if (!code || !amount || !expiry_date || !usage_limit) {
      toast.error("All fields are required.");
      return;
    }

    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    const endpoint = modalAction === "add" ? `${StandardURL}/iNyumba/promo-codes/add` : `${StandardURL}/iNyumba/promo-codes/update/${modalData.promo_code_id}`;
    const method = modalAction === "add" ? "post" : "patch";

    try {
      const response = await axios({
        method: method,
        url: endpoint,
        data: {
          code,
          amount,
          expiry_date,
          usage_limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(response.data.response);
      closeModal();
      fetchPromoCodes();
    } catch (error) {
      console.error(`Error ${modalAction}ing promo code:`, error);
      toast.error(error.response?.data?.response || `Failed to ${modalAction} promo code.`);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeletePromoCode = async (promoCodeId) => {
    if (!window.confirm("Are you sure you want to delete this promo code? This action cannot be undone.")) {
      return;
    }

    setIsActionLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.delete(
        `${StandardURL}/iNyumba/promo-codes/delete/${promoCodeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(response.data.response);
      fetchPromoCodes();
    } catch (error) {
      console.error("Error deleting promo code:", error);
      toast.error(error.response?.data?.response || "Failed to delete promo code.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const openModal = (action, promoCode = null) => {
    setModalAction(action);
    setModalData(
      promoCode
        ? { ...promoCode, expiry_date: promoCode.expiry_date.split('T')[0] }
        : { code: "", amount: "", expiry_date: "", usage_limit: "" }
    );
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalData({ code: "", amount: "", expiry_date: "", usage_limit: "" });
  };

  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setModalData({ ...modalData, [name]: value });
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">Promo Codes</h1>
            <button
              onClick={() => openModal("add")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center transition-colors duration-200"
            >
              <FaPlus className="mr-2" /> Add Promo Code
            </button>
          </div>

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : promoCodes.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No promo codes available.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage Limit</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {promoCodes.map((promoCode) => (
                      <tr key={promoCode.promo_code_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{promoCode.code}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Ksh {promoCode.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(promoCode.expiry_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{promoCode.usage_limit}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => openModal("update", promoCode)}
                            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-2 transition-colors duration-200"
                          >
                            <FaEdit className="inline mr-1" /> Update
                          </button>
                          <button
                            onClick={() => handleDeletePromoCode(promoCode.promo_code_id)}
                            disabled={isActionLoading}
                            className={`font-bold py-2 px-4 rounded transition-colors duration-200
                              ${isActionLoading ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700 text-white"}`}
                          >
                            {isActionLoading ? <Oval height={20} width={20} color="white" /> : <><FaTrash className="inline mr-1" /> Delete</>}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center">
            <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-lg w-full">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">
                {modalAction === "add" ? "Add Promo Code" : "Update Promo Code"}
              </h2>
              <div className="space-y-4">
                <input
                  type="text"
                  name="code"
                  value={modalData.code}
                  onChange={handleModalInputChange}
                  placeholder="Promo Code"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  name="amount"
                  value={modalData.amount}
                  onChange={handleModalInputChange}
                  placeholder="Amount"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  name="expiry_date"
                  value={modalData.expiry_date}
                  onChange={handleModalInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  name="usage_limit"
                  value={modalData.usage_limit}
                  onChange={handleModalInputChange}
                  placeholder="Usage Limit"
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end mt-6 space-x-2">
                <button
                  onClick={handleAction}
                  disabled={isActionLoading}
                  className={`flex items-center justify-center px-6 py-3 rounded-md font-semibold text-white transition-colors duration-200
                    ${isActionLoading
                      ? "bg-gray-400 cursor-not-allowed"
                      : modalAction === "add" ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}`}
                >
                  {isActionLoading ? (
                    <Oval height={20} width={20} color="white" ariaLabel="loading" />
                  ) : (
                    <>{modalAction === "add" ? <FaCheck className="mr-2" /> : <FaEdit className="mr-2" />} {modalAction === "add" ? "Add" : "Update"}</>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className="flex items-center justify-center px-6 py-3 rounded-md font-semibold bg-gray-500 hover:bg-gray-600 text-white transition-colors duration-200"
                >
                  <FaTimes className="mr-2" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </AuthGuard>
    </EmailGuard>
  );
};

export default PromoCodesPage;